import _ from "lodash";
import moment from "moment";
import "moment-timezone";
import {
    EmployerCensusFileState,
    EmployerCensusFile,
    EmployerCensusFileApi,
    EmployerCensusFileResponse,
    CensusUploadStatus,
    CensusUploadType
} from "@/employer/types";
import * as UserService from "@/shared/api/userService";
import { FetchState } from "@/shared/types";
import { downloadCsvFile } from "@/shared/fileDownload";
import { defineStore } from "pinia";
import { usePageSizeStore } from "@/shared/store/pageSize";
import { useNotifierStore } from "@/shared/store/notifier";

export const initialState: EmployerCensusFileState = {
    fileFetchState: FetchState.Uninitialized,
    fileDetailsFetchState: FetchState.Uninitialized,
    selectedFileId: null,
    files: [],
    pages: [],
    fileDetails: [],
    totalRecords: 0,
    pageNumber: 1,
    showHowItWorks: true,
    hasPendingFile: false
};


function mapFileForDisplay (file: EmployerCensusFileApi): EmployerCensusFile {
    let uploadDate = "";
    let uploadTime = "";
    if (file.dateUploaded) {
        const dateTimeObject = moment.tz(moment.utc(file.dateUploaded), moment.tz.guess());
        uploadDate = dateTimeObject.format("MM/DD/YYYY");
        uploadTime = dateTimeObject.format("hh:mm a");
    }
    return {
        employerCensusFileId: file.employerCensusFileId,
        fileName: file.fileName,
        uploadDate,
        uploadTime,
        uploadStatus: file.uploadStatus,
        uploadType: file.censusUploadType,
        updatedUserCount: file.updatedEmployees || 0,
        newUserCount: file.newEmployees || 0,
        errorCount: file.errorCount || 0,
        unchangedUserCount: file.unchangedEmployees || 0
    };
}

export const useCensusFileStore = defineStore("censusFile", {
    state: (): EmployerCensusFileState => (_.cloneDeep(initialState)),
    getters: {
        fileListLoaded: state => state.fileFetchState === FetchState.Loaded,
        fileListLoading: state => state.fileFetchState === FetchState.Loading,
        hasFilePendingInProcess: state => state.hasPendingFile,
        fileDetailsLoaded: state => state.fileDetailsFetchState === FetchState.Loaded,
        fileDetailsLoading: state => state.fileDetailsFetchState === FetchState.Loading,
        selectedFileDetails: state => {
            const record = _.find(state.fileDetails, item => item.employerCensusFileId === state.selectedFileId);

            return record as EmployerCensusFile;
        },
        pageCount () {
            const pageSizeStore = usePageSizeStore();
            const pageSize = pageSizeStore.pageSize;
            return this.totalRecords < pageSize ? 1 : Math.floor((this.totalRecords + pageSize - 1) / pageSize);
        },
        censusFilesForPage: state => (): EmployerCensusFile[] => {
            const pageObject = state.pages[state.pageNumber];

            if (typeof pageObject === "undefined") {
                return [];
            }
            return pageObject;
        }
    },
    actions: {
        setFileFetchState (fetchState: FetchState) {
            this.fileFetchState = fetchState;
        },
        setFileDetailsFetchState (fetchState: FetchState) {
            this.fileDetailsFetchState = fetchState;
        },
        setShowHowItWorks (display: boolean) {
            this.showHowItWorks = display;
        },
        setSelectedFile (file: EmployerCensusFile) {
            this.selectedFileId = file.employerCensusFileId;
        },
        addFiles (response: EmployerCensusFileResponse) {
            // A new record was added by another system user so we need to refresh all the stored pages.
            if (this.totalRecords !== response.totalRecords) {
                this.pages = [];
            }
            const pages = [ ...this.pages ];
            pages[this.pageNumber] = response.files.map(mapFileForDisplay);
            this.totalRecords = response.totalRecords;
            this.pages = pages;
            this.hasPendingFile = response.hasPending;
        },
        addFileDetails (fileDetails: EmployerCensusFileApi) {
            this.fileDetails = [
                ...this.fileDetails,
                mapFileForDisplay(fileDetails)
            ];
        },
        resetFileState () {
            this.fileDetails = [];
            this.fileDetailsFetchState = FetchState.Uninitialized;
        },
        clearUploadCensusFilesState () {
            this.files = [];
            this.pageNumber = 1;
            this.pages = [];
            this.totalRecords = 0;
            this.fileFetchState = FetchState.Uninitialized;
        },
        setPageNumber (pageNumber: number) {
            this.pageNumber = pageNumber;
        },
        setSelectedFileId (selectedFileId: number) {
            this.selectedFileId = selectedFileId;
        },
        downloadCensusFileResult (args: { employerId: number, censusFileId: number }): Promise<void> {
            const notifierStore = useNotifierStore();
            return UserService.downloadCensusFileResult(args.employerId, args.censusFileId).then(response => {
                const fileName = `employer-${args.employerId}-census-file-${args.censusFileId}-result.csv`;
                downloadCsvFile(response.data, fileName);
            }).catch(() => {
                notifierStore.setDanger("Could not download the census results at this time. Please try again later.");
            });
        },
        loadFileList (args: { employerId: number, pageNumber: number }) {
            return new Promise<void>((resolve, reject) => {
                this.setPageNumber(args.pageNumber);
                const pageObject = this.pages[this.pageNumber || 1];
                if (typeof pageObject !== "undefined") {
                    resolve();
                    return;
                } else {
                    const pageSizeStore = usePageSizeStore();
                    this.setFileFetchState(FetchState.Loading);
                    UserService
                        .getEmployerCensusFiles(args.employerId, args.pageNumber, pageSizeStore.pageSize)
                        .then(result => {
                            this.addFiles(result.data);
                            this.setFileFetchState(FetchState.Loaded);
                        })
                        .catch(err => reject({ message: "User api returned non-OK status", payload: err }));
                }
            });
        },
        reloadFileList (employerId: number) {
            this.clearUploadCensusFilesState();
            this.setFileFetchState(FetchState.Loading);
            const pageSizeStore = usePageSizeStore();
            return UserService
                .getEmployerCensusFiles(employerId, this.pageNumber, pageSizeStore.pageSize)
                .then(result => {
                    this.addFiles(result.data);
                    this.setFileFetchState(FetchState.Loaded);
                })
                .catch(err => Promise.reject({ message: "User api returned non-OK status", payload: err }));
        },
        loadFileDetails (args: { employerId: number, censusFileId: number }) {
            this.setSelectedFileId(args.censusFileId);

            if (this.fileDetailsFetchState === FetchState.Loading) {
                return Promise.resolve();
            }

            // File details previously loaded, return early
            const fileDetails = _.find(this.fileDetails,
                x => x.employerCensusFileId === args.censusFileId);
            if (fileDetails !== undefined || args.censusFileId === 0) {
                return Promise.resolve();
            }

            // File is pending and doesn't have results, return early
            const listFile = _.find(this.pages[this.pageNumber],
                x => x.employerCensusFileId === args.censusFileId);
            if (listFile !== undefined && listFile.uploadStatus === CensusUploadStatus.Pending) {
                const pendingStateFile: EmployerCensusFileApi = {
                    employerId: args.employerId,
                    employerCensusFileId: args.censusFileId,
                    fileName: "",
                    dateUploaded: "",
                    uploadedBy: "",
                    uploadStatus: CensusUploadStatus.Pending,
                    censusUploadType: CensusUploadType.Web,
                    updatedEmployees: 0,
                    newEmployees: 0,
                    errorCount: 0,
                    unchangedEmployees: 0
                };
                this.addFileDetails(pendingStateFile);
                this.setFileDetailsFetchState(FetchState.Loaded);
                return Promise.resolve();
            }

            this.setFileDetailsFetchState(FetchState.Loading);
            const notifierStore = useNotifierStore();
            return UserService
                .getEmployerCensusFileDetails(args.employerId, args.censusFileId)
                .then(result => {
                    this.addFileDetails(result.data.file);
                })
                .catch(err => {
                    notifierStore.setDanger(
                        "Could not retrieve the census results at this time. Please try again later.");
                    return Promise.reject({ message: "User api returned non-OK status", payload: err });
                })
                .finally(() => this.setFileDetailsFetchState(FetchState.Loaded));
        }
    }
});